import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fetchItem, fetchContent } from "@/lib/api";
import { itemPath } from "@/lib/categories";
import { resolveImage } from "@/lib/images";
import { MandalaMotif, Divider } from "@/components/MotifBackground";
import ShareButton from "@/components/ShareButton";
import Seo from "@/components/Seo";
import { CalendarDays, Sparkles, ArrowRight, Flame, ScrollText, Music, BookOpen, Waves, ListChecks, Check, ChevronRight } from "lucide-react";
import { pickScript } from "@/lib/bilingual";

// Ganesh Chaturthi 2026 — Sthapana Mon 14 Sep 2026, 11:02 AM IST; Anant Chaturdashi visarjan Fri 25 Sep 2026.
const STHAPANA_ISO = "2026-09-14T05:32:00Z"; // 11:02 AM IST == 05:32 UTC
const VISARJAN_ISO = "2026-09-25T18:30:00Z"; // end of Fri 25 Sep IST (Anant Chaturdashi)

// Devotional artwork (generated, Tanjore-inspired, on-brand parchment palette)
const GC_HERO = "https://customer-assets-0z36b82j.emergentagent.net/job_katha-explore/artifacts/tbiq1koi_file_000000008650821188d08f504d1489eb.png";
const GC_ALTAR = "https://customer-assets-0z36b82j.emergentagent.net/job_katha-explore/artifacts/cwjq71vz_file_00000000ee40821195c9c8416c57ff5c.png";

const AARTI_HI = [
  "जय गणेश, जय गणेश, जय गणेश देवा।",
  "माता जाकी पार्वती, पिता महादेवा॥",
  "एकदन्त दयावन्त, चार भुजाधारी।",
  "माथे सिंदूर सोहे, मूसे की सवारी॥",
  "पान चढ़े, फल चढ़े, और चढ़े मेवा।",
  "लड्डुअन का भोग लगे, सन्त करें सेवा॥",
  "अन्धन को आँख देत, कोढ़िन को काया।",
  "बाँझन को पुत्र देत, निर्धन को माया॥",
  "सूर श्याम शरण आए, सफल कीजे सेवा।",
  "माता जाकी पार्वती, पिता महादेवा॥",
];

const FAQ = [
  {
    q_en: "Is a clay Ganesha better than a Plaster of Paris murti?",
    q_hi: "क्या मिट्टी की गणेश प्रतिमा प्लास्टर की तुलना में श्रेष्ठ है?",
    a_en: "Yes — a natural clay (शाडु/काली मिट्टी) murti is scripturally recommended and environmentally responsible. PoP idols do not dissolve in water and are increasingly restricted.",
    a_hi: "हाँ — शाडु मिट्टी अथवा काली मिट्टी की प्रतिमा शास्त्रीय दृष्टि से श्रेष्ठ एवं पर्यावरण-सम्मत है। प्लास्टर की मूर्तियाँ जल में विलीन नहीं होतीं और अनेक स्थानों पर अब निषिद्ध हैं।",
  },
  {
    q_en: "Why should one avoid gazing at the moon on Ganesh Chaturthi?",
    q_hi: "गणेश चतुर्थी को चंद्र-दर्शन क्यों नहीं करना चाहिए?",
    a_en: "Traditionally, gazing at the moon on Bhadrapada Shukla Chaturthi is said to invite mithya-dosha (unwarranted accusation). Recite the 'Sinha prasenam avadhi…' shloka if it happens inadvertently.",
    a_hi: "भाद्रपद शुक्ल चतुर्थी को चन्द्र-दर्शन से 'मिथ्या-दोष' (झूठे लांछन) की परम्परा है। यदि अनजाने में दर्शन हो जाए तो 'सिंह: प्रसेनम् अवधीत्…' श्लोक का पाठ करें।",
  },
  {
    q_en: "How many days should I keep Ganesha at home?",
    q_hi: "गणेशजी को घर में कितने दिन विराजमान रखा जाए?",
    a_en: "Kula-parampara varies — 1½, 3, 5, 7 or the full 10 days ending on Anant Chaturdashi. Any of these is traditional; keep what your family follows.",
    a_hi: "कुल-परम्परा पर निर्भर — डेढ़, तीन, पाँच, सात अथवा पूरे दस दिन (अनन्त चतुर्दशी तक)। किसी भी अवधि की अपनी शास्त्रीय परम्परा है; अपने परिवार की प्रथा का पालन करें।",
  },
  {
    q_en: "Can I perform the puja alone without a purohit?",
    q_hi: "क्या मैं बिना पुरोहित के स्वयं पूजा कर सकता हूँ?",
    a_en: "Absolutely. A gṛhastha (householder) is traditionally empowered to perform the household Ganesh Chaturthi puja using the sequence in our detailed Pooja Vidhi entry — with sincerity and clean intent, no pandit is required.",
    a_hi: "अवश्य। गृहस्थ को अपनी शुद्ध भावना एवं संकल्प से गणेश चतुर्थी की गृह-पूजा करने का पूर्ण अधिकार है। हमारी विस्तृत पूजा-विधि प्रविष्टि का अनुसरण करें।",
  },
];

const useCountdown = (iso) => {
  const target = useMemo(() => new Date(iso).getTime(), [iso]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { diff, days, hours, minutes, seconds };
};

const Chip = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 border border-line bg-parchment/70 px-3 py-1 text-xs text-ink-2">
    {Icon && <Icon className="h-3.5 w-3.5 text-saffron" />} {children}
  </span>
);

export default function GaneshChaturthiHome() {
  const { t, lang, pick, pickTitle, pickDesc, isDeva } = useLang();
  const [puja, setPuja] = useState(null);
  const [stotras, setStotras] = useState([]);
  const [mantras, setMantras] = useState([]);
  const [katha, setKatha] = useState(null);
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dharmartha_gc26_checklist") || "{}"); } catch { return {}; }
  });
  const [faqOpen, setFaqOpen] = useState(null);

  const cd = useCountdown(STHAPANA_ISO);

  useEffect(() => {
    fetchItem("ganesh-chaturthi-puja-vidhi").then(setPuja).catch(() => {});
    fetchContent({ deity: "Ganesha", limit: 30 }).then((docs) => {
      const s = docs.filter((d) => d.type === "stotra");
      const m = docs.filter((d) => d.type === "mantra");
      const k = docs.find((d) => d.type === "katha");
      setStotras(s);
      setMantras(m);
      if (k) setKatha(k);
    }).catch(() => {});
  }, []);

  const toggle = (i) => {
    const next = { ...checked, [i]: !checked[i] };
    setChecked(next);
    localStorage.setItem("dharmartha_gc26_checklist", JSON.stringify(next));
  };

  const materials = puja?.materials || [];
  const stepsCount = puja?.steps?.length || 0;
  const mu = puja?.muhurta;

  const scrollToId = (sel) => {
    const el = document.querySelector(sel);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="ganesh-chaturthi-hub" className="pb-24">
      <Seo path="/ganesh-chaturthi" type="article" />
      {/* ─── HERO ─────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b border-line bg-[var(--surface)]">
        <MandalaMotif className="pointer-events-none absolute right-[38%] -top-24 hidden h-[560px] w-[560px] text-[var(--gold)] opacity-[0.05] md:block" />
        <div className="relative z-[2] mx-auto grid grid-cols-1 max-w-6xl items-center gap-12 px-5 py-14 md:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] md:gap-16 md:px-8 md:py-20 lg:py-24">
          {/* Left · copy */}
          <div className="fade-up">
            <p className="overline-label text-saffron">{pick("पावन पर्व · २०२६", "Sacred festival · 2026")}</p>
            <h1 className={`mt-4 text-5xl leading-[1.04] text-ink deva-lh md:text-6xl lg:text-7xl ${isDeva ? "font-deva" : "font-serif-latin"}`}>
              {pick("गणेश चतुर्थी", "Ganesh Chaturthi")}
            </h1>
            {!isDeva && (
              <p className="mt-2 font-deva text-2xl text-saffron deva-lh md:text-3xl">गणेश चतुर्थी २०२६</p>
            )}
            <div className="mt-6 h-px w-20 bg-[var(--gold)]/60" />
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-2">
              {pick(
                "गृह-स्थापना से विसर्जन तक — शुभ मुहूर्त, सामग्री, षोडशोपचार, स्तोत्र और आरती — गणेश चतुर्थी की पूर्ण मार्गदर्शिका।",
                "A complete home guide — from sthapana to visarjan, muhurta and materials, shodashopachara, stotras and arati — everything for Ganesh Chaturthi in one place."
              )}
            </p>

            {/* Countdown / active-festival / past states */}
            {cd.diff > 0 ? (
              <div className="mt-9 border-t border-line pt-6" data-testid="gc-countdown">
                <p className="overline-label text-ink-3">{pick("स्थापना तक", "Until Sthapana")}</p>
                <div className="mt-3 flex items-baseline gap-5">
                  {[
                    [cd.days, pick("दिन", "days")],
                    [cd.hours, pick("घण्टे", "hrs")],
                    [cd.minutes, pick("मिनट", "min")],
                    [cd.seconds, pick("सेकण्ड", "sec")],
                  ].map(([n, l], i) => (
                    <div key={i} className="text-center">
                      <p className="font-serif-latin text-4xl leading-none text-ink md:text-5xl">{String(n).padStart(2, "0")}</p>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-ink-3">{l}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-sm text-ink-3">{pick("सोमवार, १४ सितम्बर २०२६ · ११:०२ पूर्वाह्न (IST)", "Mon, 14 September 2026 · 11:02 AM IST")}</p>
              </div>
            ) : Date.now() < new Date(VISARJAN_ISO).getTime() ? (
              <div className="mt-9 inline-flex items-center gap-3 border border-saffron/40 bg-[rgba(199,80,0,0.05)] px-5 py-3" data-testid="gc-active">
                <Flame className="h-5 w-5 text-saffron" />
                <div>
                  <p className="font-serif-latin text-lg text-ink">{pick("पर्व चल रहा है — गणपति बाप्पा मोरया!", "The festival is live — Ganpati Bappa Morya!")}</p>
                  <p className="text-xs text-ink-3">{pick("अनन्त चतुर्दशी विसर्जन: शुक्रवार, २५ सितम्बर", "Visarjan on Anant Chaturdashi · Fri, 25 Sep")}</p>
                </div>
              </div>
            ) : (
              <p className="mt-9 max-w-xl text-ink-2" data-testid="gc-post">{pick("इस वर्ष का पर्व सम्पन्न — यह मार्गदर्शिका सम्पूर्ण वर्ष हेतु सुरक्षित रखी जा सकती है।", "This year's celebration has concluded — bookmark this guide for future observances.")}</p>
            )}

            <div className="mt-9 flex flex-wrap items-center gap-3">
              {puja ? (
                <Link to={itemPath(puja)} data-testid="gc-cta-puja" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
                  <Flame className="h-4 w-4" /> {pick("पूजा विधि आरम्भ करें", "Begin the Pooja Vidhi")}
                </Link>
              ) : (
                <button type="button" onClick={() => scrollToId("#puja")} data-testid="gc-cta-puja" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
                  <Flame className="h-4 w-4" /> {pick("पूजा विधि आरम्भ करें", "Begin the Pooja Vidhi")}
                </button>
              )}
              <button type="button" onClick={() => scrollToId("#stotras")} className="inline-flex items-center gap-2 border border-line px-6 py-3 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink">
                <ScrollText className="h-4 w-4" /> {pick("स्तोत्र-मंत्र संग्रह", "Stotras & Mantras")}
              </button>
              <ShareButton title="Ganesh Chaturthi 2026 · Dharmartha" text={pick("गणेश चतुर्थी २०२६ की सम्पूर्ण मार्गदर्शिका", "The complete guide to Ganesh Chaturthi 2026")} label={pick("साझा करें", "Share")} testid="gc-share" />
            </div>
          </div>

          {/* Right · framed devotional illustration */}
          <div className="fade-up relative order-first mx-auto w-full max-w-[20rem] md:order-none md:max-w-[26rem]" style={{ animationDelay: "0.12s" }}>
            <div className="pointer-events-none absolute -inset-2 border border-[var(--gold)]/25" aria-hidden="true" />
            <div className="relative overflow-hidden border border-[var(--gold)]/50 bg-[var(--parchment)] shadow-[0_18px_50px_rgba(44,38,33,0.16)]">
              <img src={GC_HERO} alt={pick("श्री गणेश", "Lord Ganesha")} className="block h-auto w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ─── MUHURTA STRIP ───────────────────────────────────── */}
      {mu && (
        <section data-testid="gc-muhurta" className="border-b border-line bg-[rgba(184,134,11,0.05)]">
          <div className="mx-auto grid max-w-6xl gap-6 px-5 py-10 md:grid-cols-2 md:px-8 md:py-14">
            <div className="flex gap-4">
              <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--gold)] text-gold"><CalendarDays className="h-5 w-5" /></div>
              <div>
                <p className="overline-label text-ink-3">{pick("स्थापना", "Sthapana")}</p>
                <p className="mt-1 font-serif-latin text-xl text-ink">{mu.sthapana_date}</p>
                <p className="mt-0.5 text-sm text-ink-2">{mu.sthapana_window}</p>
                <p className="mt-3 max-w-md text-[13px] italic text-ink-3">{lang === "hi" ? mu.notes_hi : mu.notes_en}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-saffron text-saffron"><Waves className="h-5 w-5" /></div>
              <div>
                <p className="overline-label text-ink-3">{pick("विसर्जन", "Visarjan")}</p>
                <p className="mt-1 font-serif-latin text-xl text-ink">{mu.visarjan_date}</p>
                <p className="mt-0.5 text-sm text-ink-2">{mu.visarjan_window}</p>
                <p className="mt-3 text-[13px] italic text-ink-3">{pick("परिवार की कुल-परम्परा के अनुसार डेढ़, तीन, पाँच, सात अथवा दस दिन।", "1½, 3, 5, 7 or 10-day traditions are all valid — follow your kula-parampara.")}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── GUIDED JOURNEY (3 phases) ───────────────────────── */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-20">
        <div className="mb-10 flex items-center gap-3">
          <span className="h-px flex-1 bg-line" />
          <span className="overline-label text-gold flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> {pick("मार्गदर्शित यात्रा", "Guided Journey")}</span>
          <span className="h-px flex-1 bg-line" />
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { i: "01", key: "prepare", en: "Prepare", hi: "तैयारी", desc_en: "Gather your samagri and set up the altar", desc_hi: "सामग्री एकत्र करें एवं वेदी सजाएँ", cta_en: "Continue", cta_hi: "आगे बढ़ें", icon: ListChecks, scroll: "#materials" },
            { i: "02", key: "perform", en: "Perform the Pooja", hi: "पूजा सम्पादन", desc_en: `${stepsCount || 30}-step shodashopachara vidhi`, desc_hi: `${stepsCount || 30}-चरण षोडशोपचार विधि`, cta_en: "Open Pooja Vidhi", cta_hi: "पूजा विधि खोलें", icon: Flame, to: puja ? itemPath(puja) : null, scroll: "#puja" },
            { i: "03", key: "chant", en: "Chant & Conclude", hi: "पाठ एवं समापन", desc_en: "Stotras · Atharvashirsha · Arati · Visarjan", desc_hi: "स्तोत्र · अथर्वशीर्ष · आरती · विसर्जन", cta_en: "Continue", cta_hi: "आगे बढ़ें", icon: Music, scroll: "#stotras" },
          ].map((p) => {
            const cls = "group flex w-full flex-col border border-line bg-surface p-7 text-left transition-all duration-500 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-[0_8px_30px_rgba(44,38,33,0.08)]";
            const inner = (
              <>
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--gold)]/50 font-serif-latin text-sm text-gold">{p.i}</span>
                  <span className="h-px flex-1 bg-[var(--line)]" />
                  <p.icon className="h-4 w-4 text-saffron" />
                </div>
                <h3 className={`mt-6 text-2xl text-ink deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick(p.hi, p.en)}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-2">{pick(p.desc_hi, p.desc_en)}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-saffron transition-transform group-hover:translate-x-0.5">{pick(p.cta_hi, p.cta_en)} <ArrowRight className="h-4 w-4" /></span>
              </>
            );
            return p.to ? (
              <Link key={p.key} to={p.to} data-testid={`gc-phase-${p.key}`} className={cls}>{inner}</Link>
            ) : (
              <button key={p.key} type="button" onClick={() => scrollToId(p.scroll)} data-testid={`gc-phase-${p.key}`} className={cls}>{inner}</button>
            );
          })}
        </div>
      </section>

      {/* ─── MATERIALS CHECKLIST ─────────────────────────────── */}
      {materials.length > 0 && (
        <section id="materials" className="border-y border-line bg-[var(--surface)]">
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-20">
            <div className="mb-8 flex items-baseline gap-3">
              <span className="h-px w-10 bg-[var(--gold)]" />
              <h2 className={`text-3xl text-saffron deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("पूजा सामग्री सूची", "Pooja Materials Checklist")}</h2>
              <span className="overline-label text-ink-3">{materials.length} {pick("वस्तुएँ", "items")}</span>
            </div>
            <p className="max-w-2xl text-ink-3">{pick("तैयारी में मदद के लिए — प्रत्येक वस्तु पर टैप करें। आपकी सूची इस उपकरण में सुरक्षित रहेगी।", "Tap each item as you gather it — your checklist is saved on this device.")}</p>
            <ul className="mt-8 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {materials.map((m, i) => (
                <li key={i}>
                  <button
                    data-testid={`gc-checklist-${i}`}
                    onClick={() => toggle(i)}
                    className={`group flex w-full items-start gap-3 border p-3.5 text-left transition-all ${
                      checked[i]
                        ? "border-[var(--gold)] bg-[rgba(184,134,11,0.08)]"
                        : "border-line bg-parchment/60 hover:border-ink-3"
                    }`}
                  >
                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border transition-colors ${checked[i] ? "border-[var(--gold)] bg-[var(--gold)] text-white" : "border-ink-3"}`}>
                      {checked[i] && <Check className="h-3.5 w-3.5" />}
                    </span>
                    <span className={`flex-1 text-[15px] leading-snug ${checked[i] ? "text-ink line-through decoration-[var(--gold)]/60" : "text-ink-2"}`}>{pickScript(m, lang)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ─── PUJA VIDHI CTA ──────────────────────────────────── */}
      {puja && (
        <section id="puja" className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-20">
          <div className="relative grid grid-cols-1 items-center overflow-hidden border border-line bg-gradient-to-br from-[rgba(199,80,0,0.05)] via-[var(--parchment)] to-[rgba(184,134,11,0.06)] md:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
            <div className="relative p-8 md:p-12">
              <p className="overline-label text-saffron">{pick("मुख्य विधि", "The main procedure")}</p>
              <h2 className={`mt-3 text-3xl leading-tight text-ink deva-lh md:text-4xl ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pickTitle(puja)}</h2>
              <p className="mt-4 max-w-2xl text-ink-2">{pickDesc(puja)}</p>
              <div className="mt-6 flex flex-wrap gap-2.5">
                <Chip icon={ListChecks}>{materials.length} {pick("सामग्री", "materials")}</Chip>
                <Chip icon={Flame}>{stepsCount} {pick("चरण", "steps")}</Chip>
                <Chip icon={ScrollText}>{(puja.mantras || []).length} {pick("मंत्र", "mantras")}</Chip>
                <Chip icon={Sparkles}>{pick("संकल्प + अथर्वशीर्ष", "Sankalpa + Atharvashirsha")}</Chip>
              </div>
              <Link to={itemPath(puja)} data-testid="gc-open-puja" className="mt-8 inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
                {pick("पूर्ण पूजा विधि खोलें", "Open the full Pooja Vidhi")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="relative overflow-hidden border-t border-line md:border-l md:border-t-0">
              <img src={GC_ALTAR} alt={pick("गणेश पूजा वेदी", "Ganesha puja altar")} loading="lazy" className="block h-auto w-full object-contain" />
            </div>
          </div>
        </section>
      )}

      {/* ─── STOTRAS & MANTRAS GRID ──────────────────────────── */}
      {(stotras.length > 0 || mantras.length > 0) && (
        <section id="stotras" className="border-y border-line bg-[var(--surface)]">
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-20">
            <div className="mb-10 flex items-baseline gap-3">
              <span className="h-px w-10 bg-[var(--gold)]" />
              <h2 className={`text-3xl text-saffron deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("गणेश स्तोत्र एवं मंत्र", "Ganesha Stotras & Mantras")}</h2>
              <span className="overline-label text-ink-3">{stotras.length + mantras.length} {pick("पाठ", "recitals")}</span>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[...stotras, ...mantras].map((item) => (
                <Link key={item.slug} to={itemPath(item)} data-testid={`gc-stotra-${item.slug}`} className="group flex flex-col border border-line bg-parchment/60 p-6 transition-all duration-500 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-[0_8px_30px_rgba(44,38,33,0.08)]">
                  <div className="flex items-center justify-between">
                    <span className="overline-label text-gold">{item.type === "mantra" ? pick("मंत्र", "Mantra") : pick("स्तोत्र", "Stotra")}</span>
                    {item.read_time && <span className="text-xs text-ink-3">{item.read_time}</span>}
                  </div>
                  <h3 className={`mt-4 text-xl text-ink deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pickTitle(item)}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-2 line-clamp-3">{pickDesc(item)}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-saffron transition-transform group-hover:translate-x-0.5">{pick("पाठ करें", "Read & recite")} <ArrowRight className="h-4 w-4" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── AARTI PANEL ─────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-20">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] md:gap-14">
          <div>
            <p className="overline-label text-saffron">{pick("संध्या-आरती", "The Evening Arati")}</p>
            <h2 className={`mt-3 text-3xl leading-tight text-ink deva-lh md:text-4xl ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("जय गणेश देवा", "Jai Ganesh Deva")}</h2>
            <p className="mt-5 max-w-md leading-relaxed text-ink-2">{pick("प्रत्येक दिन प्रातः एवं सायं यह पारम्परिक आरती परिवार सहित गाएँ — पाँच-मुखी दीप अथवा कर्पूर से भगवान की परिक्रमा करते हुए।", "Sing this traditional arati morning and evening with the family — circling Ganesha with a five-wick lamp or camphor flame.")}</p>
            <p className="mt-6 text-sm italic text-ink-3">{pick("माता जाकी पार्वती, पिता महादेवा — इस पंक्ति पर विशेष श्रद्धा से।", "Slow the tempo at 'माता जाकी पार्वती, पिता महादेवा' — the devotional heart of the arati.")}</p>
          </div>
          <div className="relative border-l-2 border-[var(--gold)] bg-[var(--surface-alt)]/40 px-6 py-8 md:px-10 md:py-10" data-testid="gc-aarti-block">
            <div className="space-y-2 font-deva text-lg leading-[2] text-ink">
              {AARTI_HI.map((line, i) => (
                <p key={i} className={i % 2 === 1 ? "pl-4 text-ink-2" : ""}>{line}</p>
              ))}
            </div>
            <p className="mt-6 border-t border-[var(--line)] pt-4 text-xs text-ink-3">{pick("तत्पश्चात् कर्पूर आरती — ॐ श्रीगणपतये नमः। कर्पूरनीराजनं समर्पयामि॥", "Follow with camphor arati — ॐ श्रीगणपतये नमः। कर्पूरनीराजनं समर्पयामि॥")}</p>
          </div>
        </div>
      </section>

      {/* ─── STORY / KATHA ───────────────────────────────────── */}
      {katha && (
        <section className="border-y border-line bg-[var(--surface)]">
          <div className="mx-auto grid grid-cols-1 max-w-6xl gap-10 px-5 py-16 md:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] md:px-8 md:py-20">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={resolveImage(katha)} alt={pickTitle(katha)} loading="lazy" className="h-full w-full object-cover grayscale-[0.1]" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
            </div>
            <div className="flex flex-col justify-center">
              <p className="overline-label text-gold flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" /> {pick("कथा · परम्परा", "Katha · Tradition")}</p>
              <h2 className={`mt-3 text-3xl leading-tight text-ink deva-lh md:text-4xl ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pickTitle(katha)}</h2>
              <p className="mt-5 max-w-2xl leading-relaxed text-ink-2">{pickDesc(katha)}</p>
              <Link to={itemPath(katha)} data-testid="gc-open-katha" className="mt-6 inline-flex w-fit items-center gap-2 border border-line px-5 py-2.5 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink">
                {pick("कथा पढ़ें", "Read the katha")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ─── VISARJAN CARD ───────────────────────────────────── */}
      {mu && (
        <section className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-20">
          <div className="relative overflow-hidden border border-saffron/30 bg-[rgba(199,80,0,0.04)] p-8 md:p-12">
            <p className="overline-label text-saffron flex items-center gap-2"><Waves className="h-3.5 w-3.5" /> {pick("विसर्जन", "Visarjan")}</p>
            <h2 className={`mt-3 text-3xl leading-tight text-ink deva-lh md:text-4xl ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("आदरपूर्वक पुनरागमन", "A reverent farewell")}</h2>
            <p className="mt-4 max-w-2xl text-ink-2">{pick("अंतिम दिन दीप, धूप, पुष्प, दूर्वा एवं नैवेद्य अर्पित कर अंतिम आरती करें। तत्पश्चात्:", "On the final day offer lamp, incense, flowers, durva and naivedya, perform the final arati, and recite:")}</p>
            <blockquote className="mt-5 max-w-2xl border-l-2 border-saffron bg-parchment/70 px-5 py-4 font-deva text-lg leading-[1.9] text-ink">
              ॐ श्रीगणपतये नमः।{"\n"}यथास्थानं प्रतिष्ठापयामि।{"\n"}पुनरागमनाय च॥
            </blockquote>
            <p className="mt-6 font-deva text-xl text-saffron">गणपति बाप्पा मोरया · मंगलमूर्ति मोरया</p>
            <p className="mt-5 max-w-2xl text-[13px] italic text-ink-3">{pick("पर्यावरण-हितैषी उत्सव के लिए मिट्टी की प्रतिमा चुनें एवं नगरपालिका के निर्धारित कृत्रिम विसर्जन-कुंड का उपयोग करें।", "For an eco-friendly celebration use a natural clay murti and, where available, an authorised artificial immersion tank.")}</p>
          </div>
        </section>
      )}

      {/* ─── FAQ ─────────────────────────────────────────────── */}
      <section className="border-t border-line bg-[var(--surface)]">
        <div className="mx-auto max-w-4xl px-5 py-16 md:px-8 md:py-20">
          <p className="overline-label text-gold">{pick("प्रश्नोत्तर", "Frequently asked")}</p>
          <h2 className={`mt-3 text-3xl leading-tight text-ink deva-lh md:text-4xl ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("आम प्रश्न", "Common questions")}</h2>
          <div className="mt-10 divide-y divide-line border-y border-line">
            {FAQ.map((f, i) => (
              <button key={i} data-testid={`gc-faq-${i}`} onClick={() => setFaqOpen(faqOpen === i ? null : i)} className="flex w-full items-start justify-between gap-6 py-6 text-left transition-colors hover:bg-parchment/50">
                <div className="flex-1">
                  <p className={`text-lg text-ink ${lang === "en" ? "font-serif-latin" : "font-deva-body"}`}>{pick(f.q_hi, f.q_en)}</p>
                  {faqOpen === i && (
                    <p className="mt-3 max-w-3xl leading-relaxed text-ink-2">{pick(f.a_hi, f.a_en)}</p>
                  )}
                </div>
                <ChevronRight className={`mt-1 h-5 w-5 shrink-0 text-ink-3 transition-transform ${faqOpen === i ? "rotate-90" : ""}`} />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOOTER CHANT ────────────────────────────────────── */}
      <section className="mx-auto max-w-4xl px-5 py-16 text-center md:px-8">
        <Divider className="mx-auto mb-10 max-w-xs" />
        <p className="font-deva text-3xl text-saffron md:text-4xl">वक्रतुण्ड महाकाय सूर्यकोटि समप्रभ।</p>
        <p className="mt-2 font-deva text-3xl text-saffron md:text-4xl">निर्विघ्नं कुरु मे देव शुभकार्येषु सर्वदा॥</p>
        <p className="mt-6 text-sm italic text-ink-3">{pick("शुभ पर्व की मंगलमय शुभकामनाएँ।", "May your celebration be blessed with grace and clarity.")}</p>
      </section>
    </div>
  );
}
